/* eslint-disable indent */
import {
    ENTITY_TYPE, FONT_ICON, IMMUTABLE, INLINE_REFERENCE_TYPE, STYLE_AUTO,
} from '../tettra-editor/utilities/CONSTANTS';

const legacyInlineReferenceTypes = [
    ENTITY_TYPE.GITHUB_ISSUE,
    ENTITY_TYPE.PAGE_REFERENCE,
    ENTITY_TYPE.GOOGLE_DOC,
    ENTITY_TYPE.MENTION,
];

/**
 * A map of inline reference entity sub-types (i.e., `entity.data.type`) to their font icon unicode characters
 */
const inlineReferenceIcon = {
    [ENTITY_TYPE.GITHUB_ISSUE]: FONT_ICON.GITHUB,
    [ENTITY_TYPE.PAGE_REFERENCE]: FONT_ICON.DOCUMENT,
    [ENTITY_TYPE.MENTION]: '@',
};

/**
 * Get unicode font icon character for the given Google Drive file MIME type.
 *
 * @param {string} mimeType Google Drive file MIME type (see https://developers.google.com/drive/api/v3/mime-types)
 * @returns {string|undefined} Google Drive icon for the given MIME type, or the default Google Drive icon.
 */

const getGoogleDriveIcon = (mimeType) => {
    switch (mimeType) {
        case 'application/vnd.google-apps.drawing':
            return FONT_ICON.GOOGLE_DRAWING;
        case 'application/vnd.google-apps.spreadsheet':
            return FONT_ICON.GOOGLE_SHEETS;
        case 'application/vnd.google-apps.presentation':
            return FONT_ICON.GOOGLE_SLIDES;
        case 'application/vnd.google-apps.form':
            return FONT_ICON.GOOGLE_FORMS;
        case 'application/vnd.google-apps.document':
            return FONT_ICON.GOOGLE_DOCS;
        default:
            return FONT_ICON.GOOGLE_DRIVE_DEFAULT;
    }
};

const FONT_ICON_LENGTH = 1;

const isInlineReferenceEntity = rawEntity => legacyInlineReferenceTypes.includes(rawEntity.type);

/**
 * Returns an array of the following objects for each legacy inline reference entity detected,
 * or undefined if none found:
 *
 * <pre>
 *     {
 *         entity: {
 *             // raw entity
 *         },
 *         block: {
 *             // entity's matching entry from rawContentState.blocks
 *         },
 *         range: {
 *             // entity's matching entry from rawContentState.blocks.entityRanges
 *         },
 *     }
 *  </pre>
 */
const getLegacyInlineReferenceEntities = (rawContentState) => {
    const { blocks, entityMap } = rawContentState;
    const legacyInlineReferenceList = [];
    blocks.forEach((rawContentBlock) => {
        const { entityRanges } = rawContentBlock;
        if (entityRanges) {
            Object.values(entityRanges).forEach((entityRange) => {
                const currentEntity = entityMap[entityRange.key];
                if (isInlineReferenceEntity(currentEntity)) {
                    legacyInlineReferenceList.push({
                        entity: currentEntity,
                        block: rawContentBlock,
                        range: entityRange,
                    });
                }
            });
        }
    });
    return legacyInlineReferenceList.length > 0 ? legacyInlineReferenceList : undefined;
};

const getGithubApiUrl = (data) => {
    if (data.apiUrl) return data.apiUrl;
    if (!data.url) return null;
    const url = new URL(data.url);

    return `${url.protocol}//api.github.com/repos${url.pathname}`;
};

const getInlineReferenceEntityData = (rawLegacyEntity) => {
    const { data } = rawLegacyEntity;
    switch (rawLegacyEntity.type) {
        case ENTITY_TYPE.GITHUB_ISSUE:
            return {
                referenceType: INLINE_REFERENCE_TYPE.GITHUB_ISSUE,
                title: data.title,
                url: data.url,
                apiUrl: getGithubApiUrl(data),
                metadata: {
                    number: data.number,
                },
                isConvertedFromLegacy: true,
            };
        case ENTITY_TYPE.PAGE_REFERENCE:
            return {
                referenceType: INLINE_REFERENCE_TYPE.PAGE_REFERENCE,
                title: data.title,
                url: data.url,
                metadata: {
                    category: data.category,
                },
                isConvertedFromLegacy: true,
            };
        case ENTITY_TYPE.GOOGLE_DOC:
            return {
                referenceType: INLINE_REFERENCE_TYPE.GOOGLE_DOC,
                title: data.name,
                url: data.webViewLink,
                metadata: {
                    mimeType: data.mimeType,
                    createdAt: data.createdAt,
                    modifiedAt: data.modifiedTime,
                    iconLink: data.iconLink,
                },
                isConvertedFromLegacy: true,
            };
        case ENTITY_TYPE.MENTION:
            return {
                referenceType: INLINE_REFERENCE_TYPE.MENTION,
                title: data.display_name,
                url: data.url,
                metadata: {
                    displayName: data.display_name,
                    email: data.email,
                    firstName: data.first_name,
                    image192: data.image_192,
                    lastName: data.last_name,
                    slackId: data.slack_id,
                },
                isConvertedFromLegacy: true,
            };
        default:
            return rawLegacyEntity;
    }
};

const getInlineReferenceEntity = rawLegacyEntity => ({
        type: ENTITY_TYPE.INLINE_REFERENCE,
        data: getInlineReferenceEntityData(rawLegacyEntity),
        mutability: IMMUTABLE,
    });

const getInlineReferenceIcon = (rawLegacyEntity) => {
    const { type, data: { mimeType } } = rawLegacyEntity;
    return inlineReferenceIcon[type] || (mimeType && getGoogleDriveIcon(mimeType)) || FONT_ICON.DOCUMENT;
};

/**
 * Convert a legacy inline reference entity from raw data
 *
 * @param legacyEntity An object with the following shape: `{ entity, block, range }`
 */
const convertRawEntityToInlineReference = (legacyEntity) => {
    const { range, entity, block } = legacyEntity;
    // User mention entities already have their "icon" inserted (the `@` symbol in default font)
    if (entity.type !== ENTITY_TYPE.MENTION) {
        // Insert font icon character into block text
        block.text = [block.text.slice(0, range.offset), getInlineReferenceIcon(entity), block.text.slice(range.offset)].join('');
        range.length += FONT_ICON_LENGTH;
        // Adjust all subsequent entity range offsets to account for inserted character
        Object.values(block.entityRanges).forEach((entityRange) => {
            if (entityRange.offset > range.offset) entityRange.offset += FONT_ICON_LENGTH;
        });
        block.inlineStyleRanges.forEach((inlineStyleRange) => {
            if (inlineStyleRange.offset > range.offset) inlineStyleRange.offset += FONT_ICON_LENGTH;
        });
    }
    // Apply inline style to icon so Draft.js will wrap it in an HTML element we can uniquely style
    block.inlineStyleRanges.push({ offset: range.offset, length: FONT_ICON_LENGTH, style: STYLE_AUTO.FONT_ICON });

    return Object.assign(entity, getInlineReferenceEntity(entity));
};

const convertLegacyInlineReferences = (rawContentState) => {
    const legacyInlineReferences = getLegacyInlineReferenceEntities(rawContentState);
    if (!legacyInlineReferences) return rawContentState;
    legacyInlineReferences.forEach((entity) => {
        Object.assign(entity, convertRawEntityToInlineReference(entity));
    });
    return rawContentState;
};

const transformCommentHtml = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const links = doc.querySelectorAll('a');
    links.forEach(link => {
        if (link.innerHTML.includes('')) {
            link.innerHTML = link.innerHTML.replace('', '<img class="question-page-icon" height="18" width="18" src="/img/icons/file-icon-page.png">');
            link.classList.add('question-page-link');
        }
    });

    return doc.body.innerHTML;
}

export { getGoogleDriveIcon, transformCommentHtml };
export default convertLegacyInlineReferences;
